import { MediaCollection, PaginatedResponse, User } from '@arpeggi/shared'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { useMediaCollections } from '../../../../features/media-queries'
import { silentAuthenticatedGet } from '../../../hooks/useAuth'

const UNLIMITED = 1000000
const PER_PAGE = 10

const PARAMS = {
  perPage: UNLIMITED
}

const getPackHolders = async (editionId: number) => {
  const url = `/api/v2/editions/${editionId}/holders`
  const { data } = await silentAuthenticatedGet<PaginatedResponse<User>>(url, { params: PARAMS })
  return data.data
}

const useMCData = () => {
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useMediaCollections({
    params: { perPage: PER_PAGE }
  })
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage()
  }, [hasNextPage, isFetchingNextPage])
  const flattenedData = data?.pages.flatMap((page) => page.data.map((model) => model)) || []
  return { data: flattenedData, isLoading }
}

const CSV_HEADERS = 'wallet_address, display_name, email\n'

const generateCSV = (users: User[]) => {
  const csv = users
    .map((user) => `${user.walletAddress},${user.displayName},${user.email}`)
    .join('\n')
  return CSV_HEADERS + csv
}

const downloadBlob = (filename: string, data: string) => {
  const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  URL.revokeObjectURL(url)
}

const getFilename = (mc: MediaCollection) => {
  const slug = mc.slug
  const date = format(new Date(), 'yyyy/MM/dd')
  const filename = `${slug}-holders-${date}.csv`
  return filename
}

const downloadHoldersForPack = async (mc: MediaCollection) => {
  const id = mc.edition?.id
  const holders = await getPackHolders(id)
  const csv = generateCSV(holders)
  return downloadBlob(getFilename(mc), csv)
}

export const usePackHolders = () => {
  const { data, isLoading } = useMCData()
  const [isDownloading, setIsDownloading] = useState(false)
  const label = isLoading
    ? 'Loading...'
    : isDownloading
    ? 'Downloading...'
    : 'Download Holders list for pack'
  const loading = isLoading || isDownloading
  const bindSelect = (mc: MediaCollection) => async (evt) => {
    setIsDownloading(true)
    await downloadHoldersForPack(mc)
    setIsDownloading(false)
  }
  return { data, loading, bindSelect, label }
}
